import { Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap"
import DashboardDropdown from "./DashboardDropdown"
import Permissions from "../../constants/Permissions"
import useAppContext from "../../hooks/useAppContext"
import { ChangeEvent, useEffect, useState } from "react"
import { addMonths } from "date-fns/esm"
import Configs from "../../Configs"
import VoucherService from "../../services/VoucherService"
import { BarChart, DatasetChart, LineChart } from "../../components/ChartJS"
import Utils from "../../utils/Utils"
import ContentBody from "../../components/ContentBody"
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { getReportItemText } from "../../constants/ReportItems"

function MonthlyVoucherDashboard() {
    const { state } = useAppContext()
    const [dateReport, setDateReport] = useState<{
        startDate: Date | null,
        endDate: Date | null
    }>({
        startDate: addMonths(new Date(), -Configs.DEFAULT_LAST_MONTHS + 1),
        endDate: new Date()
    })

    const GetMonthlyActiveVouchersReport = async (startYear: number, startMonth: number, endYear: number, endMonth: number) => {
        if (state.currentProviderId) {
            const res = await VoucherService.monthlyActiveVouchersReport(state.currentProviderId, startYear, startMonth, endYear, endMonth)

            if (res?.isSuccess) {
                setReportData(res.data)
            } else {
                setReportData(null)
                console.log(res.message);
            }
        }
    }

    useEffect(() => {
        if (state.currentProviderId) {
            if (dateReport.startDate && dateReport.endDate) {
                var startYear = dateReport.startDate.getFullYear()
                var startMonth = dateReport.startDate.getMonth() + 1
                var endYear = dateReport.endDate.getFullYear()
                var endMonth = dateReport.endDate.getMonth() + 1

                GetMonthlyActiveVouchersReport(startYear, startMonth, endYear, endMonth)
            }
        }
    }, [state.currentProviderId, dateReport])

    const [reportData, setReportData] = useState<{
        date: Date,
        quantity: number,
        amount: number,
        paymentService: string,
        placeId: number,
        placeName: string
    }[] | null>(null)

    const [currentPlaceId, setCurrentPlaceId] = useState<number | null>(null)
    const [places, setPlaces] = useState<{
        id: number,
        name: string
    }[] | null>(null)

    useEffect(() => {
        if (reportData && reportData.length) {
            setCurrentPlaceId(null)
            generateVoucherQuantityChartData(null)
            generateVoucherAmountChartData(null)
            generateVoucherAmountByPlacesSummaryChartData()
            generateVoucherQuantityByPlacesSummaryChartData()

            setPlaces(
                reportData
                    .map((x) => { return { id: x.placeId, name: x.placeName } })
                    .filter((value, index, self) => self.findIndex(x => x.id === value.id) === index)
            )
        } else {
            setCurrentPlaceId(null)
            setPlaces(null)
            setVoucherQuantityChartData({ ...voucherQuantityChartData, labels: [], datasets: [] })
            setVoucherAmountChartData({ ...voucherAmountChartData, labels: [], datasets: [] })
            setVoucherAmountByPlacesSummaryChartData({ ...voucherAmountByPlacesSummaryChartData, labels: [], datasets: [] })
            setVoucherQuantityByPlacesSummaryChartData({ ...voucherQuantityByPlacesSummaryChartData, labels: [], datasets: [] })
        }
    }, [reportData])

    const handlePlaceChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setCurrentPlaceId(e.target.value ? parseInt(e.target.value) : null)
    }

    useEffect(() => {
        generateVoucherQuantityChartData(currentPlaceId)
        generateVoucherAmountChartData(currentPlaceId)
    }, [currentPlaceId])

    const [voucherQuantityChartData, setVoucherQuantityChartData] = useState({
        title: "SALES VOLUME",
        labels: [] as string[],
        datasets: [] as DatasetChart[]
    })

    const generateVoucherQuantityChartData = (placeId: number | null) => {
        const labels = [] as string[]
        const datasets: DatasetChart[] = []

        if (reportData) {
            var data = reportData.filter(x => x.placeId === (placeId ? placeId : x.placeId))

            //dates
            var dates = data.map((x) => x.date).filter((value, index, self) => self.indexOf(value) === index).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

            dates.forEach(date => {
                //labels
                labels.push(Utils.dateToString(date, Configs.MONTH_FORMAT))
            })

            //paymentServices
            var paymentServices = data.map((x) => x.paymentService).filter((value, index, self) => self.indexOf(value) === index).sort()
            paymentServices.forEach(paymentService => {
                const dataset = {
                    label: paymentService,
                    data: []
                } as DatasetChart

                dates.forEach(date => {
                    var items = data.filter(x => x.paymentService === paymentService && x.date === date)

                    var quantity = 0
                    if (items) {
                        quantity = items.map(x => x.quantity).reduce((a, b) => a + b, 0)
                    }
                    dataset.data.push(quantity)
                })

                datasets.push(dataset)
            })

            //Total
            const dataset = {
                label: "All",
                data: []
            } as DatasetChart

            dates.forEach(date => {
                var items = data.filter(x => x.date === date)

                var quantity = 0
                if (items) {
                    quantity = items.map(x => x.quantity).reduce((a, b) => a + b, 0)
                }
                dataset.data.push(quantity)
            })

            datasets.push(dataset)

            setVoucherQuantityChartData({ ...voucherQuantityChartData, labels: labels, datasets: datasets })
        } else {
            setVoucherQuantityChartData({ ...voucherQuantityChartData, labels: [], datasets: [] })
        }
    }

    const [voucherAmountChartData, setVoucherAmountChartData] = useState({
        title: "SALES REVENUE",
        labels: [] as string[],
        datasets: [] as DatasetChart[]
    })

    const generateVoucherAmountChartData = (placeId: number | null) => {
        const labels = [] as string[]
        const datasets: DatasetChart[] = []

        if (reportData) {
            var data = reportData.filter(x => x.placeId === (placeId ? placeId : x.placeId))

            //dates
            var dates = data.map((x) => x.date).filter((value, index, self) => self.indexOf(value) === index).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

            dates.forEach(d => {
                //labels
                labels.push(Utils.dateToString(d, Configs.MONTH_FORMAT))
            })

            //paymentServices
            var paymentServices = data.map((x) => x.paymentService).filter((value, index, self) => self.indexOf(value) === index).sort()
            paymentServices.forEach(paymentService => {
                const dataset = {
                    label: paymentService,
                    data: []
                } as DatasetChart

                dates.forEach(date => {
                    var items = data.filter(x => x.paymentService === paymentService && x.date === date)

                    var amount = 0
                    if (items) {
                        amount = items.map(x => x.amount).reduce((a, b) => a + b, 0)
                    }
                    dataset.data.push(amount)
                })

                datasets.push(dataset)
            })

            //Total
            const dataset = {
                label: "All",
                data: []
            } as DatasetChart

            dates.forEach(date => {
                var items = data.filter(x => x.date === date)

                var amount = 0
                if (items) {
                    amount = items.map(x => x.amount).reduce((a, b) => a + b, 0)
                }
                dataset.data.push(amount)
            })

            datasets.push(dataset)

            setVoucherAmountChartData({ ...voucherAmountChartData, labels: labels, datasets: datasets })
        } else {
            setVoucherAmountChartData({ ...voucherAmountChartData, labels: [], datasets: [] })
        }
    }

    const handleChange = ([startDate, endDate]: [Date | null, Date | null]) => {
        setDateReport({
            startDate: startDate,
            endDate: endDate
        })
    }

    const handleExportExcel = () => {
        if (state.currentProviderId) {
            if (dateReport.startDate && dateReport.endDate) {
                var startYear = dateReport.startDate.getFullYear()
                var startMonth = dateReport.startDate.getMonth() + 1
                var endYear = dateReport.endDate.getFullYear()
                var endMonth = dateReport.endDate.getMonth() + 1

                VoucherService.exportMonthlyActiveVouchersReport2Excel(state.currentProviderId, startYear, startMonth, endYear, endMonth)
            }
        }
    }

    //Places Summary Report
    const [voucherAmountByPlacesSummaryChartData, setVoucherAmountByPlacesSummaryChartData] = useState({
        title: "SALES REVENUE BY PLACE",
        labels: [] as string[],
        datasets: [] as DatasetChart[]
    })

    const generateVoucherAmountByPlacesSummaryChartData = () => {
        const labels = [] as string[]
        const datasets: DatasetChart[] = []

        if (reportData) {
            var myPlaces = reportData
                .map((x) => { return { id: x.placeId, name: x.placeName } })
                .filter((value, index, self) => self.findIndex(x => x.id === value.id) === index)

            var paymentServices = reportData.map((x) => x.paymentService)
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort()

            myPlaces.forEach(place => {
                //labels
                labels.push(place.name)
            })

            //paymentServices
            paymentServices.forEach(paymentService => {
                const dataset = {
                    label: paymentService,
                    data: []
                } as DatasetChart

                myPlaces.forEach(place => {
                    var items = reportData.filter(x => x.paymentService === paymentService && x.placeId === place.id)

                    var amount = 0
                    if (items) {
                        amount = items.map(x => x.amount).reduce((a, b) => a + b, 0)
                    }
                    dataset.data.push(amount)
                })

                datasets.push(dataset)
            })

            setVoucherAmountByPlacesSummaryChartData({ ...voucherAmountByPlacesSummaryChartData, labels: labels, datasets: datasets })
        } else {
            setVoucherAmountByPlacesSummaryChartData({ ...voucherAmountByPlacesSummaryChartData, labels: [], datasets: [] })
        }
    }

    const [voucherQuantityByPlacesSummaryChartData, setVoucherQuantityByPlacesSummaryChartData] = useState({
        title: "SALES VOLUME BY PLACE",
        labels: [] as string[],
        datasets: [] as DatasetChart[]
    })

    const generateVoucherQuantityByPlacesSummaryChartData = () => {
        const labels = [] as string[]
        const datasets: DatasetChart[] = []

        if (reportData) {
            var myPlaces = reportData
                .map((x) => { return { id: x.placeId, name: x.placeName } })
                .filter((value, index, self) => self.findIndex(x => x.id === value.id) === index)

            var paymentServices = reportData.map((x) => x.paymentService)
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort()

            myPlaces.forEach(place => {
                //labels
                labels.push(place.name)
            })

            //paymentServices
            paymentServices.forEach(paymentService => {
                const dataset = {
                    label: paymentService,
                    data: []
                } as DatasetChart

                myPlaces.forEach(place => {
                    var items = reportData.filter(x => x.paymentService === paymentService && x.placeId === place.id)

                    var quantity = 0
                    if (items) {
                        quantity = items.map(x => x.quantity).reduce((a, b) => a + b, 0)
                    }
                    dataset.data.push(quantity)
                })

                datasets.push(dataset)
            })

            setVoucherQuantityByPlacesSummaryChartData({ ...voucherQuantityByPlacesSummaryChartData, labels: labels, datasets: datasets })
        } else {
            setVoucherQuantityByPlacesSummaryChartData({ ...voucherQuantityByPlacesSummaryChartData, labels: [], datasets: [] })
        }
    }

    return (
        <>
            <section className="content-header">
                <Container fluid>
                    <Row className="mb-2">
                        <Col sm={12} style={{ display: "flex", alignItems: "center" }}>
                            <h1>{getReportItemText(Permissions.AllVouchers.MonthlyVouchersReport)}</h1>
                            <DashboardDropdown reportName={Permissions.AllVouchers.MonthlyVouchersReport}></DashboardDropdown>
                        </Col>
                    </Row>
                </Container>
            </section>

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    <DatePicker
                                        onChange={handleChange}
                                        selectsRange
                                        startDate={dateReport.startDate}
                                        endDate={dateReport.endDate}
                                        dateFormat="MM/yyyy"
                                        showMonthYearPicker
                                        className="form-control"
                                    />
                                    <Form.Select className="ml-3" style={{ width: "auto", display: "inline-block" }} value={currentPlaceId || ""} onChange={handlePlaceChange}>
                                        <option value="">All places</option>
                                        {places && places.map(place => {
                                            return <option value={place.id} key={place.id}>{place.name}</option>
                                        })}
                                    </Form.Select>

                                    <div className="card-tools" style={{ display: "flex" }}>
                                        <Button variant="outline-info" size="sm" onClick={handleExportExcel}>
                                            <i className="fas fa-file-excel"></i> Export excel
                                        </Button>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col lg={9}>
                                            <LineChart title={voucherAmountChartData.title} labels={voucherAmountChartData.labels} datasets={voucherAmountChartData.datasets}></LineChart>
                                        </Col>
                                        <Col lg={3}>
                                            <Table responsive className="mt-2" size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Payment</th>
                                                        <th style={{ textAlign: "right" }}>Revenue</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {voucherAmountChartData.datasets.filter(x => x.label !== "All").map((ds, index) => {
                                                        return <tr key={index}>
                                                            <td>{ds.label}</td>
                                                            <td style={{ textAlign: "right" }}>{Utils.formatNumber(ds.data.reduce((a, b) => a + b, 0))}</td>
                                                        </tr>
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>All</th>
                                                        <th style={{ textAlign: "right" }}>{Utils.formatNumber(voucherAmountChartData.datasets.filter(x => x.label !== "All").map(ds => ds.data).flat().reduce((a, b) => a + b, 0))}</th>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col lg={9}>
                                            <LineChart title={voucherQuantityChartData.title} labels={voucherQuantityChartData.labels} datasets={voucherQuantityChartData.datasets}></LineChart>
                                        </Col>
                                        <Col lg={3}>
                                            <Table responsive className="mt-2" size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Payment</th>
                                                        <th style={{ textAlign: "right" }}>Volume</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {voucherQuantityChartData.datasets.filter(x => x.label !== "All").map((ds, index) => {
                                                        return <tr key={index}>
                                                            <td>{ds.label}</td>
                                                            <td style={{ textAlign: "right" }}>{Utils.formatNumber(ds.data.reduce((a, b) => a + b, 0))}</td>
                                                        </tr>
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <th>All</th>
                                                        <th style={{ textAlign: "right" }}>{Utils.formatNumber(voucherQuantityChartData.datasets.filter(x => x.label !== "All").map(ds => ds.data).flat().reduce((a, b) => a + b, 0))}</th>
                                                    </tr>
                                                </tfoot>
                                            </Table>
                                        </Col>
                                    </Row>
                                    {places && places.length > 1 && <>
                                        <Row>
                                            <Col>
                                                <BarChart
                                                    title={voucherAmountByPlacesSummaryChartData.title}
                                                    labels={voucherAmountByPlacesSummaryChartData.labels}
                                                    datasets={voucherAmountByPlacesSummaryChartData.datasets}
                                                ></BarChart>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <BarChart
                                                    title={voucherQuantityByPlacesSummaryChartData.title}
                                                    labels={voucherQuantityByPlacesSummaryChartData.labels}
                                                    datasets={voucherQuantityByPlacesSummaryChartData.datasets}
                                                ></BarChart>
                                            </Col>
                                        </Row>
                                    </>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>
        </>
    )
}

export default MonthlyVoucherDashboard